/**
 * Volume tab — executive-level KPIs, MoM/QoQ/YoY deltas, line chart + bar chart + heat map.
 * Mirrors `rrm-exec-home-volume.html`.
 */

import { KpiTile } from '@/components/primitives';
import type { RrmVolumeKpis, RrmFilters } from './types';

const MOCK_KPIS: RrmVolumeKpis = {
  accountsOpened: 312,
  accountsOpenedMoMPct: 12,
  accountsOpenedQoQPct: -18,
  depositBalanceMillions: 84.2,
  depositBalanceMoMPct: 4.2,
  depositBalanceYoYPct: 11,
  newCustomers: 187,
  newCustomersMoMPct: 8,
  newCustomersQoQPct: 0,
  revenueFeesPlusNIIKDollars: 142,
  revenueMoMPct: 6.3,
  revenueYoYPct: -2,
};

export interface VolumeTabProps {
  filters: RrmFilters;
}

export function VolumeTab({ filters: _filters }: VolumeTabProps): JSX.Element {
  const k = MOCK_KPIS;
  return (
    <>
      <div className="rrm-kpi-strip">
        <KpiTile
          label="Accounts Opened"
          value={k.accountsOpened}
          severity="default"
          delta={{ dir: k.accountsOpenedMoMPct >= 0 ? 'up' : 'down', text: `${k.accountsOpenedMoMPct}% MoM` }}
          sparkline={
            <svg viewBox="0 0 100 32" preserveAspectRatio="none" width={100} height={32} aria-hidden>
              <polyline points="0,24 20,22 40,18 60,16 80,12 100,8" stroke="var(--ent-loan)" strokeWidth={2} fill="none" />
              <polyline points="0,24 20,22 40,18 60,16 80,12 100,8 100,32 0,32" fill="rgba(41,104,173,0.12)" stroke="none" />
            </svg>
          }
        />
        <KpiTile
          label="Deposit Balance"
          value={`$${k.depositBalanceMillions}M`}
          severity="balance"
          delta={{ dir: 'up', text: `${k.depositBalanceMoMPct}% MoM` }}
          sparkline={
            <svg viewBox="0 0 100 32" preserveAspectRatio="none" width={100} height={32} aria-hidden>
              <polyline points="0,22 20,20 40,18 60,14 80,11 100,7" stroke="var(--ent-deposit)" strokeWidth={2} fill="none" />
              <polyline points="0,22 20,20 40,18 60,14 80,11 100,7 100,32 0,32" fill="rgba(3,131,135,0.12)" stroke="none" />
            </svg>
          }
        />
        <KpiTile
          label="New Customers"
          value={k.newCustomers}
          severity="cust"
          delta={{ dir: 'up', text: `${k.newCustomersMoMPct}% MoM` }}
          sparkline={
            <svg viewBox="0 0 100 32" preserveAspectRatio="none" width={100} height={32} aria-hidden>
              <polyline points="0,18 20,19 40,16 60,14 80,15 100,10" stroke="var(--ent-relationship)" strokeWidth={2} fill="none" />
              <polyline points="0,18 20,19 40,16 60,14 80,15 100,10 100,32 0,32" fill="rgba(56,103,50,0.12)" stroke="none" />
            </svg>
          }
        />
        <KpiTile
          label="Revenue (Fees + NII)"
          value={`$${k.revenueFeesPlusNIIKDollars}K`}
          severity="rev"
          delta={{ dir: k.revenueMoMPct >= 0 ? 'up' : 'down', text: `${k.revenueMoMPct}% MoM` }}
          sparkline={
            <svg viewBox="0 0 100 32" preserveAspectRatio="none" width={100} height={32} aria-hidden>
              <polyline points="0,20 20,17 40,19 60,15 80,12 100,10" stroke="var(--ent-ira)" strokeWidth={2} fill="none" />
              <polyline points="0,20 20,17 40,19 60,15 80,12 100,10 100,32 0,32" fill="rgba(185,119,14,0.12)" stroke="none" />
            </svg>
          }
        />
      </div>

      <div className="rrm-chart-row">
        <article className="rrm-chart-card" aria-labelledby="rrm-line-h">
          <header className="rrm-chart-header">
            <div>
              <div className="rrm-chart-title" id="rrm-line-h">Accounts opened — by month (rolling 12)</div>
              <div className="rrm-chart-sub">Dashed line = prior year</div>
            </div>
          </header>
          <div className="rrm-chart-body">
            <svg viewBox="0 0 680 260" width="100%" height={260} role="img" aria-label="Line chart — accounts opened rolling 12 months">
              <line x1={40} y1={30} x2={670} y2={30} stroke="var(--ink-200)" />
              <line x1={40} y1={80} x2={670} y2={80} stroke="var(--ink-200)" />
              <line x1={40} y1={130} x2={670} y2={130} stroke="var(--ink-200)" />
              <line x1={40} y1={180} x2={670} y2={180} stroke="var(--ink-200)" />
              <line x1={40} y1={230} x2={670} y2={230} stroke="var(--ink-200)" />

              <text x={30} y={34} textAnchor="end" fontSize={10} fill="var(--ink-500)">500</text>
              <text x={30} y={84} textAnchor="end" fontSize={10} fill="var(--ink-500)">400</text>
              <text x={30} y={134} textAnchor="end" fontSize={10} fill="var(--ink-500)">300</text>
              <text x={30} y={184} textAnchor="end" fontSize={10} fill="var(--ink-500)">200</text>
              <text x={30} y={234} textAnchor="end" fontSize={10} fill="var(--ink-500)">100</text>

              <polyline points="60,155 110,145 160,140 210,150 260,130 310,145 360,120 410,110 460,115 510,125 560,115 620,135" stroke="var(--ink-400)" strokeWidth={1.5} strokeDasharray="4 3" fill="none" />
              <polyline points="60,170 110,160 160,155 210,145 260,140 310,125 360,110 410,95 460,115 510,130 560,110 620,98 620,240 60,240" fill="rgba(41,104,173,0.12)" />
              <polyline points="60,170 110,160 160,155 210,145 260,140 310,125 360,110 410,95 460,115 510,130 560,110 620,98" stroke="var(--pcb-blue)" strokeWidth={2.5} fill="none" />

              {[60, 110, 160, 210, 260, 310, 360, 410, 460, 510, 560].map((x, i) => (
                <circle key={x} cx={x} cy={[170, 160, 155, 145, 140, 125, 110, 95, 115, 130, 110][i]} r={4} fill="var(--pcb-blue)" stroke="var(--surface-card)" strokeWidth={2} />
              ))}
              <circle cx={620} cy={98} r={5} fill="var(--pcb-blue-dark)" stroke="var(--surface-card)" strokeWidth={2} />

              {['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'].map((m, i) => (
                <text key={m} x={60 + i * 50} y={250} textAnchor="middle" fontSize={10} fill={m === 'Apr' ? 'var(--pcb-blue-dark)' : 'var(--ink-500)'} fontWeight={m === 'Apr' ? 700 : 400}>{m}</text>
              ))}
              <text x={620} y={88} textAnchor="middle" fontSize={10} fontWeight={700} fill="var(--pcb-blue-dark)">312</text>
            </svg>
          </div>
        </article>

        <article className="rrm-chart-card" aria-labelledby="rrm-bar-h">
          <header className="rrm-chart-header">
            <div>
              <div className="rrm-chart-title" id="rrm-bar-h">By product</div>
              <div className="rrm-chart-sub">MTD account-opening breakdown</div>
            </div>
          </header>
          <div className="rrm-chart-body">
            <svg viewBox="0 0 380 260" width="100%" height={260} role="img" aria-label="Bar chart — accounts opened by product">
              <line x1={50} y1={20} x2={370} y2={20} stroke="var(--ink-200)" />
              <line x1={50} y1={70} x2={370} y2={70} stroke="var(--ink-200)" />
              <line x1={50} y1={120} x2={370} y2={120} stroke="var(--ink-200)" />
              <line x1={50} y1={170} x2={370} y2={170} stroke="var(--ink-200)" />
              <line x1={50} y1={220} x2={370} y2={220} stroke="var(--ink-200)" />
              {[
                { x: 60, h: 160, y: 60, fill: 'var(--ent-loan)', val: 162, label: 'DDA' },
                { x: 115, h: 110, y: 110, fill: 'var(--ent-deposit)', val: 62, label: 'SAV' },
                { x: 170, h: 95, y: 125, fill: 'var(--ent-relationship)', val: 51, label: 'CD' },
                { x: 225, h: 40, y: 180, fill: 'var(--ent-ira)', val: 18, label: 'IRA' },
                { x: 280, h: 30, y: 190, fill: 'var(--ent-safedeposit)', val: 11, label: 'SDB' },
                { x: 335, h: 25, y: 195, fill: 'var(--ent-biz)', val: 8, label: 'Biz' },
              ].map((b) => (
                <g key={b.label}>
                  <rect x={b.x} y={b.y} width={40} height={b.h} fill={b.fill} />
                  <text x={b.x + 20} y={b.y - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--ink-900)">{b.val}</text>
                  <text x={b.x + 20} y={236} textAnchor="middle" fontSize={10} fontWeight={600} fill="var(--ink-600)">{b.label}</text>
                </g>
              ))}
            </svg>
          </div>
        </article>
      </div>

      <article className="rrm-chart-card" aria-labelledby="rrm-heat-h">
        <header className="rrm-chart-header">
          <div>
            <div className="rrm-chart-title" id="rrm-heat-h">Branch × Product heat map — accounts opened (MTD)</div>
            <div className="rrm-chart-sub">Darker = higher volume</div>
          </div>
        </header>
        <div className="rrm-chart-body" style={{ padding: 0 }}>
          <table className="rrm-heat-table">
            <thead>
              <tr>
                <th className="row-header" style={{ width: 180 }}>Branch</th>
                <th>DDA</th><th>SAV</th><th>CD</th><th>IRA</th><th>SDB</th><th>Business</th>
                <th style={{ background: 'var(--ink-100)' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['01 — Rolla Downtown', 78, 24, 17, 9, 4, 3, 135],
                ['02 — Hwy 72', 42, 18, 14, 5, 3, 2, 84],
                ['03 — St. James', 24, 12, 10, 2, 2, 1, 51],
                ['04 — Vienna', 10, 5, 6, 1, 1, 1, 24],
                ['05 — Edgar Springs', 8, 3, 4, 1, 1, 1, 18],
              ].map((row) => (
                <tr key={String(row[0])}>
                  <td className="row-header">{row[0]}</td>
                  {row.slice(1, -1).map((v, j) => {
                    const n = Number(v);
                    const cls = n >= 61 ? 'rrm-heat-6' : n >= 41 ? 'rrm-heat-5' : n >= 21 ? 'rrm-heat-4' : n >= 11 ? 'rrm-heat-3' : n >= 6 ? 'rrm-heat-2' : 'rrm-heat-1';
                    return <td key={j} className={cls}>{n}</td>;
                  })}
                  <td style={{ background: 'var(--ink-100)', fontWeight: 800 }}>{row[row.length - 1]}</td>
                </tr>
              ))}
              <tr style={{ background: 'var(--ink-50)', fontWeight: 800 }}>
                <td className="row-header">Total</td>
                <td>162</td><td>62</td><td>51</td><td>18</td><td>11</td><td>8</td>
                <td style={{ background: 'var(--brand-fill-strong)', color: 'var(--on-brand)' }}>312</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </>
  );
}
